interface Clip {
  url: string;
  languageCode: string; // ISO 639-1 or similar code
}

interface AudioClips {
  [uuid: string]: Clip;
}

// === S3 Integration Types ===

interface S3Config {
  bucketName: string;
  region: string;
  // Note: For presigned URLs, we'll use a backend service or AWS SDK
  // These would be set via environment variables or config
  accessKeyId?: string;
  secretAccessKey?: string;
  sessionToken?: string;
}

interface S3ClipMetadata {
  key: string; // S3 object key (path in bucket)
  languageCode: string;
  fileName: string;
  contentType?: string;
  size?: number;
  lastModified?: Date;
}

interface S3ClipsIndex {
  [uuid: string]: S3ClipMetadata;
}

interface PresignedUrlOptions {
  expiresIn?: number; // Seconds until URL expires (default 3600 = 1 hour)
  responseContentDisposition?: 'inline' | 'attachment';
  responseContentType?: string;
}

interface PresignedUrlResponse {
  url: string;
  expiresAt: Date;
  clipId: string;
}

interface S3ServiceError {
  code: string;
  message: string;
  statusCode?: number;
}

// === End S3 Types ===

export class AudioClipService {
  private readonly audioClips: AudioClips = {
    // Basque (Spain)
    "d4e5f6a7-b8c9-0123-4567-890abcdef123": {
      url: "https://lingguessr.s3.us-east-1.amazonaws.com/eu/clips/common_voice_eu_18324400.mp3?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjENr%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQC%2Ba%2BImhaE73lUZzkqV2QFtWzbVe0t%2Fr%2BetCnKcIGSrQAIhANjqx4Snfv5c02SKShmlnIqBn7q%2BziyPJWt3G6E6cV%2BbKr4DCPP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQABoMNjQxMTA4OTkzNzc2Igxikp50IT2Mlys5Bi0qkgOGnYU%2FJLSYtC5i29Z2i4S9Phf%2Bycvz1VoafYHE2OChsK6j%2Ft2VotMWvT8QAC%2BG%2F2fvYhhMtdbxbfcEPDvJIt4EM9bbqcAsA%2F5ugSyMZipZDVYxaJiIrwvh860nQtDNkPj3Vnjz6HUdCpQvDJ6BWucYW6K5AVNDhN089QRifiMstQSPaiF1IucA8UikqwI597V81k4nQn2hUi%2FV8zz7lXZllIQkt9fbA6OEUuqwL1u7HrD6kOF7PYlpqvttgOZLWCnUnwtzEAhkrqhF3yFYQ9LmW3qtSZEReYrpzi3fOv0s0UjJzCQShMqTKJmxHL%2Fvp47Pk9%2F4jS7kgH0tSMBH%2ByiQ%2Bw9%2BrSEQXV1GHZy6fUe31QApDdvqN%2FMqw7Vv9kGYw5cms8t%2FbCl9M30%2BNxiX4LIp8RGWtmKPQIYD32oaJLgI0xQeOQXP3R2RzHyd61ZDyyRWmj10%2FHvhnQvi9hhKHesXigtkxAtR7ilKoyT2qGZ3A1ImiJSj4jCF780lKi9PRNE2b5ldwmcfEDy%2FVoOGhvG%2FHDcwlZb%2FwwY63QLg4ZsOCbVqjOYoptO5Pzcz6nYbCVIKLACQAj%2BQrZuBDeJd0TGVJsgHuOqSAgh8xt4zNG4Hg9YFMo0rcIspiRuHgGHk2yKVJdTt4drmdRGxtQWlRLc8kG5sOxbMNOH5A6FwPzl3rlagKkXiMxKoK%2FuMK8xyXKXK87QGiVAjPGoxFIKO9SU%2FZ%2BX6w5ZO1oH2p7%2Fr6Ae0wUreoy7Q7imaqY4eT2IiPaf%2BbvMsnPtGzi2qmN5qGHJV1WNFkoUkbVvQGVPzeNbHvKKpSMe2R%2BKxo%2BSqp8PcvXYyQkzL9KQ%2FI5K%2BUVyB8Bcaee3DbEIrqqaUBw9bm0GuJcvUQWWCBXNengv3gfyJ1Ba8egrFNEeIMOr%2FRe5o5bnaax%2FZDS6rXvo%2FTFVChXXVlLTqF34OR6lXd%2BjCKSwoZ6Pjdsy3gxWBGYqjaBQpRdieUDZ2v9em%2B8eFJBGqtYLCKjSlO%2F4DKI2Q&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIAZKRIS43YAOV6RG2Y%2F20250722%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250722T173816Z&X-Amz-Expires=43200&X-Amz-SignedHeaders=host&X-Amz-Signature=32a4a7d62cb9e5a087594c4277c83b3b47e26a566927908d0b2026154b49fc78",
      languageCode: "eu",
    },
    // Hebrew (Israel)
    "a1b2c3d4-e5f6-7890-1234-567890abcdef": {
      url: "https://lingguessr.s3.us-east-1.amazonaws.com/he/clips/common_voice_he_38130919.mp3?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjENr%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQC%2Ba%2BImhaE73lUZzkqV2QFtWzbVe0t%2Fr%2BetCnKcIGSrQAIhANjqx4Snfv5c02SKShmlnIqBn7q%2BziyPJWt3G6E6cV%2BbKr4DCPP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQABoMNjQxMTA4OTkzNzc2Igxikp50IT2Mlys5Bi0qkgOGnYU%2FJLSYtC5i29Z2i4S9Phf%2Bycvz1VoafYHE2OChsK6j%2Ft2VotMWvT8QAC%2BG%2F2fvYhhMtdbxbfcEPDvJIt4EM9bbqcAsA%2F5ugSyMZipZDVYxaJiIrwvh860nQtDNkPj3Vnjz6HUdCpQvDJ6BWucYW6K5AVNDhN089QRifiMstQSPaiF1IucA8UikqwI597V81k4nQn2hUi%2FV8zz7lXZllIQkt9fbA6OEUuqwL1u7HrD6kOF7PYlpqvttgOZLWCnUnwtzEAhkrqhF3yFYQ9LmW3qtSZEReYrpzi3fOv0s0UjJzCQShMqTKJmxHL%2Fvp47Pk9%2F4jS7kgH0tSMBH%2ByiQ%2Bw9%2BrSEQXV1GHZy6fUe31QApDdvqN%2FMqw7Vv9kGYw5cms8t%2FbCl9M30%2BNxiX4LIp8RGWtmKPQIYD32oaJLgI0xQeOQXP3R2RzHyd61ZDyyRWmj10%2FHvhnQvi9hhKHesXigtkxAtR7ilKoyT2qGZ3A1ImiJSj4jCF780lKi9PRNE2b5ldwmcfEDy%2FVoOGhvG%2FHDcwlZb%2FwwY63QLg4ZsOCbVqjOYoptO5Pzcz6nYbCVIKLACQAj%2BQrZuBDeJd0TGVJsgHuOqSAgh8xt4zNG4Hg9YFMo0rcIspiRuHgGHk2yKVJdTt4drmdRGxtQWlRLc8kG5sOxbMNOH5A6FwPzl3rlagKkXiMxKoK%2FuMK8xyXKXK87QGiVAjPGoxFIKO9SU%2FZ%2BX6w5ZO1oH2p7%2Fr6Ae0wUreoy7Q7imaqY4eT2IiPaf%2BbvMsnPtGzi2qmN5qGHJV1WNFkoUkbVvQGVPzeNbHvKKpSMe2R%2BKxo%2BSqp8PcvXYyQkzL9KQ%2FI5K%2BUVyB8Bcaee3DbEIrqqaUBw9bm0GuJcvUQWWCBXNengv3gfyJ1Ba8egrFNEeIMOr%2FRe5o5bnaax%2FZDS6rXvo%2FTFVChXXVlLTqF34OR6lXd%2BjCKSwoZ6Pjdsy3gxWBGYqjaBQpRdieUDZ2v9em%2B8eFJBGqtYLCKjSlO%2F4DKI2Q&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIAZKRIS43YAOV6RG2Y%2F20250722%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250722T173957Z&X-Amz-Expires=43200&X-Amz-SignedHeaders=host&X-Amz-Signature=2f6a1de8e6ed2a337ddb3d591ba6715137cdb3c85b322f8eae0e7acf62d70855",
      languageCode: "he",
    },
    // Afrikaans (South Africa)
    "e5f6a7b8-c9d0-1234-5678-90abcdef1234": {
      url: "https://lingguessr.s3.us-east-1.amazonaws.com/af/clips/common_voice_af_40328246.mp3?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjENr%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQC%2Ba%2BImhaE73lUZzkqV2QFtWzbVe0t%2Fr%2BetCnKcIGSrQAIhANjqx4Snfv5c02SKShmlnIqBn7q%2BziyPJWt3G6E6cV%2BbKr4DCPP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQABoMNjQxMTA4OTkzNzc2Igxikp50IT2Mlys5Bi0qkgOGnYU%2FJLSYtC5i29Z2i4S9Phf%2Bycvz1VoafYHE2OChsK6j%2Ft2VotMWvT8QAC%2BG%2F2fvYhhMtdbxbfcEPDvJIt4EM9bbqcAsA%2F5ugSyMZipZDVYxaJiIrwvh860nQtDNkPj3Vnjz6HUdCpQvDJ6BWucYW6K5AVNDhN089QRifiMstQSPaiF1IucA8UikqwI597V81k4nQn2hUi%2FV8zz7lXZllIQkt9fbA6OEUuqwL1u7HrD6kOF7PYlpqvttgOZLWCnUnwtzEAhkrqhF3yFYQ9LmW3qtSZEReYrpzi3fOv0s0UjJzCQShMqTKJmxHL%2Fvp47Pk9%2F4jS7kgH0tSMBH%2ByiQ%2Bw9%2BrSEQXV1GHZy6fUe31QApDdvqN%2FMqw7Vv9kGYw5cms8t%2FbCl9M30%2BNxiX4LIp8RGWtmKPQIYD32oaJLgI0xQeOQXP3R2RzHyd61ZDyyRWmj10%2FHvhnQvi9hhKHesXigtkxAtR7ilKoyT2qGZ3A1ImiJSj4jCF780lKi9PRNE2b5ldwmcfEDy%2FVoOGhvG%2FHDcwlZb%2FwwY63QLg4ZsOCbVqjOYoptO5Pzcz6nYbCVIKLACQAj%2BQrZuBDeJd0TGVJsgHuOqSAgh8xt4zNG4Hg9YFMo0rcIspiRuHgGHk2yKVJdTt4drmdRGxtQWlRLc8kG5sOxbMNOH5A6FwPzl3rlagKkXiMxKoK%2FuMK8xyXKXK87QGiVAjPGoxFIKO9SU%2FZ%2BX6w5ZO1oH2p7%2Fr6Ae0wUreoy7Q7imaqY4eT2IiPaf%2BbvMsnPtGzi2qmN5qGHJV1WNFkoUkbVvQGVPzeNbHvKKpSMe2R%2BKxo%2BSqp8PcvXYyQkzL9KQ%2FI5K%2BUVyB8Bcaee3DbEIrqqaUBw9bm0GuJcvUQWWCBXNengv3gfyJ1Ba8egrFNEeIMOr%2FRe5o5bnaax%2FZDS6rXvo%2FTFVChXXVlLTqF34OR6lXd%2BjCKSwoZ6Pjdsy3gxWBGYqjaBQpRdieUDZ2v9em%2B8eFJBGqtYLCKjSlO%2F4DKI2Q&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIAZKRIS43YAOV6RG2Y%2F20250722%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250722T174050Z&X-Amz-Expires=43200&X-Amz-SignedHeaders=host&X-Amz-Signature=4c59251d3baa9e442e1a7acbe44f2e468a5ea6e79c7ec848d6208b5fd27f42e5",
      languageCode: "af",
    },
    // Armenian (Armenia)
    "b2c3d4e5-f6a7-8901-2345-67890abcdef1": {
      url: "https://lingguessr.s3.us-east-1.amazonaws.com/am/clips/common_voice_am_37768253.mp3?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjENr%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJGMEQCICKM8Ch5bOrydnDbk576ZJGcNHjn%2F92Uzn4muDXROIbZAiBGmZA%2Fcmu%2Fsuc%2Bsd4vbNgjeOyoXeSF6SQg5EeWmta2hCq%2BAwjz%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAAaDDY0MTEwODk5Mzc3NiIMG0N0U9LFjX90aOPvKpIDydZ5P6FtwB8MpD6qwzVlwaSFtZZuMOCxDhugLnvbJJ%2BZGJQorLwC4wVdLq5eKw1SUA%2FNzlySDAf894Q10Gcc5UNfnvoOG%2F4DVxV3MO%2B%2BZ81RDniGxw3mBB9%2BuJS%2B6d5DUJbbKrQi4GfH0svj40LU%2Fbrcnj08A7l%2BGGKQ%2BkmpoSPHT7wRAxpItgoYu5r8Hm%2F9vPUYErg2l6b1E7enDAtb7XLPV8Gq0ul2LPOJ5dAo3X81B8%2BGcXeCeyKgsHQlPHZkpdkg3xqvfYrmp%2F2m7xDNzKqUKC3becWscdg8jBrMsSOQZZkdHxvQvHAcoPc1dzXGpV8JhHetvZyf2U6OsjVF8UbQFzlxxwMctRgey1v1Kw6lfDmxlYEqpbYWLcpipr8IWhxrx4%2F2w3hg5p2cLXnbU0mCW%2BCbkrFvmeVKhEGiagnD135J0jZ8u8dwL1vopkwopZqiaJOgN8IdWoSDvFKUcbOKqzKMmdZReUz5WGZWr4dRraHJhqwF1ttQc04oQI%2BP9NbGTBNPWOFrqSBRB85vkSTRMJWW%2F8MGOt8CMRd%2BarQRrd1PObhCICetyo%2BfmaExs832EBGLcZ6KxOJYEx1q3lV78wB5noBDPjWiHicN6QmkjZPOqLfB0NwmEZ1rqeiv3UzWPqBOOum39mLgYB4TzxRNOYgQliaDZ0EfpzIaUgrzxk4Hf8qqD1YDSmj%2FrAUMywxX2IwjA75%2Bbytx%2FSlrJ5iVTwFVKf1kllE8dUnZbMDyGwRB4WUxAT%2Fk8vYcP7nm7mEtzERDlQDsMUZGoGDpAdSoGV1J7rN%2FSzHrcNvEvJTI2S62IJXwyYFyC%2FkLRz8EOXAZPJQjGt0bQ0tQRGRN4Zl7ERf2prCOBEvovnGH91%2FuTJp%2FZrN5fzzoEc4oL27NaKTBdB4BtwwaJEwBhW8%2FX06WfiT%2FxdPAD4O7pO7rJes6F%2F289LNRF15%2BJrPbmROXpruBpnu37pbov8hv71hZxwpgKDBnp2X9oddjrktLXt7bvZCL9aj8ZE8H&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIAZKRIS43YIYMANMJY%2F20250722%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250722T173425Z&X-Amz-Expires=43200&X-Amz-SignedHeaders=host&X-Amz-Signature=c135da7d3599398f5f2b54cac6a3f427cb9796bab0bdbfa68973027013b2f470",
      languageCode: "am",
    },
    // Amharic (Ethiopia)
    "c3d4e5f6-a7b8-9012-3456-7890abcdef12": {
      url: "https://lingguessr.s3.us-east-1.amazonaws.com/hy/clips/common_voice_hy-AM_28246341.mp3?response-content-disposition=inline&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Security-Token=IQoJb3JpZ2luX2VjENr%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJGMEQCICKM8Ch5bOrydnDbk576ZJGcNHjn%2F92Uzn4muDXROIbZAiBGmZA%2Fcmu%2Fsuc%2Bsd4vbNgjeOyoXeSF6SQg5EeWmta2hCq%2BAwjz%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAAaDDY0MTEwODk5Mzc3NiIMG0N0U9LFjX90aOPvKpIDydZ5P6FtwB8MpD6qwzVlwaSFtZZuMOCxDhugLnvbJJ%2BZGJQorLwC4wVdLq5eKw1SUA%2FNzlySDAf894Q10Gcc5UNfnvoOG%2F4DVxV3MO%2B%2BZ81RDniGxw3mBB9%2BuJS%2B6d5DUJbbKrQi4GfH0svj40LU%2Fbrcnj08A7l%2BGGKQ%2BkmpoSPHT7wRAxpItgoYu5r8Hm%2F9vPUYErg2l6b1E7enDAtb7XLPV8Gq0ul2LPOJ5dAo3X81B8%2BGcXeCeyKgsHQlPHZkpdkg3xqvfYrmp%2F2m7xDNzKqUKC3becWscdg8jBrMsSOQZZkdHxvQvHAcoPc1dzXGpV8JhHetvZyf2U6OsjVF8UbQFzlxxwMctRgey1v1Kw6lfDmxlYEqpbYWLcpipr8IWhxrx4%2F2w3hg5p2cLXnbU0mCW%2BCbkrFvmeVKhEGiagnD135J0jZ8u8dwL1vopkwopZqiaJOgN8IdWoSDvFKUcbOKqzKMmdZReUz5WGZWr4dRraHJhqwF1ttQc04oQI%2BP9NbGTBNPWOFrqSBRB85vkSTRMJWW%2F8MGOt8CMRd%2BarQRrd1PObhCICetyo%2BfmaExs832EBGLcZ6KxOJYEx1q3lV78wB5noBDPjWiHicN6QmkjZPOqLfB0NwmEZ1rqeiv3UzWPqBOOum39mLgYB4TzxRNOYgQliaDZ0EfpzIaUgrzxk4Hf8qqD1YDSmj%2FrAUMywxX2IwjA75%2Bbytx%2FSlrJ5iVTwFVKf1kllE8dUnZbMDyGwRB4WUxAT%2Fk8vYcP7nm7mEtzERDlQDsMUZGoGDpAdSoGV1J7rN%2FSzHrcNvEvJTI2S62IJXwyYFyC%2FkLRz8EOXAZPJQjGt0bQ0tQRGRN4Zl7ERf2prCOBEvovnGH91%2FuTJp%2FZrN5fzzoEc4oL27NaKTBdB4BtwwaJEwBhW8%2FX06WfiT%2FxdPAD4O7pO7rJes6F%2F289LNRF15%2BJrPbmROXpruBpnu37pbov8hv71hZxwpgKDBnp2X9oddjrktLXt7bvZCL9aj8ZE8H&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIAZKRIS43YIYMANMJY%2F20250722%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250722T173307Z&X-Amz-Expires=43200&X-Amz-SignedHeaders=host&X-Amz-Signature=74e6e164785d1061fd8ef088094702332bf1c6f8a23d8c2c94de1f4c01b3953c",
      languageCode: "hy",
    }
  };

  // === S3 Configuration and State ===

  private s3Config: S3Config | null = null;
  private s3ClipsIndex: S3ClipsIndex = {};
  private urlCache: Map<string, { url: string; expiresAt: Date }> = new Map();
  private useS3: boolean = false; // Toggle between hardcoded clips and S3

  // === Existing Methods ===

  /**
   * Gets all audio clips
   */
  public getAllClips(): AudioClips {
    return { ...this.audioClips };
  }

  /**
   * Gets all clip IDs
   */
  public getAllClipIds(): string[] {
    return Object.keys(this.audioClips);
  }

  /**
   * Gets a specific clip by ID
   */
  public getClipById(id: string): Clip | undefined {
    return this.audioClips[id];
  }

  /**
   * Gets the total number of clips
   */
  public getClipCount(): number {
    return Object.keys(this.audioClips).length;
  }

  /**
   * Gets clips by language code
   */
  public getClipsByLanguage(languageCode: string): { [uuid: string]: Clip } {
    const result: { [uuid: string]: Clip } = {};
    Object.entries(this.audioClips).forEach(([uuid, clip]) => {
      if (clip.languageCode === languageCode) {
        result[uuid] = clip;
      }
    });
    return result;
  }

  /**
   * Gets all unique language codes
   */
  public getAllLanguageCodes(): string[] {
    const languageCodes = new Set(Object.values(this.audioClips).map(clip => clip.languageCode));
    return Array.from(languageCodes);
  }

  // === S3 Integration Methods ===

  /**
   * Configures S3 settings for presigned URL generation
   * @param config S3 configuration object
   */
  public configureS3(config: S3Config): void {
    this.s3Config = { ...config };
    console.log(`S3 configured for bucket: ${config.bucketName} in region: ${config.region}`);
  }

  /**
   * Enables or disables S3 mode
   * @param enabled Whether to use S3 for audio clips
   */
  public setS3Mode(enabled: boolean): void {
    this.useS3 = enabled;
    if (enabled && !this.s3Config) {
      console.warn('S3 mode enabled but no S3 config provided. Call configureS3() first.');
    }
  }

  /**
   * Loads S3 clips index from a remote source or configuration
   * This would typically fetch from an API or load from a JSON file
   * @param s3Index The index of available S3 clips
   */
  public loadS3ClipsIndex(s3Index: S3ClipsIndex): void {
    this.s3ClipsIndex = { ...s3Index };
    console.log(`Loaded ${Object.keys(s3Index).length} S3 clips into index`);
  }

  /**
   * Gets a presigned URL for an S3 audio clip
   * @param clipId The ID of the clip to get a presigned URL for
   * @param options Optional parameters for the presigned URL
   * @returns Promise that resolves to a presigned URL response
   */
  public async getPresignedUrl(
    clipId: string, 
    options: PresignedUrlOptions = {}
  ): Promise<PresignedUrlResponse> {
    if (!this.s3Config) {
      throw new Error('S3 not configured. Call configureS3() first.');
    }

    if (!this.s3ClipsIndex[clipId]) {
      throw new Error(`Clip ${clipId} not found in S3 index`);
    }

    // Check cache first
    const cached = this.urlCache.get(clipId);
    if (cached && cached.expiresAt > new Date()) {
      return {
        url: cached.url,
        expiresAt: cached.expiresAt,
        clipId
      };
    }

    // TODO: Implement actual S3 presigned URL generation
    // This would use AWS SDK or call a backend service
    try {
      const presignedUrl = await this.generatePresignedUrl(clipId, options);
      
      // Cache the URL
      this.urlCache.set(clipId, {
        url: presignedUrl.url,
        expiresAt: presignedUrl.expiresAt
      });

      return presignedUrl;
    } catch (error) {
      throw this.handleS3Error(error);
    }
  }

  /**
   * Gets multiple presigned URLs in batch
   * @param clipIds Array of clip IDs
   * @param options Optional parameters for the presigned URLs
   * @returns Promise that resolves to an array of presigned URL responses
   */
  public async getPresignedUrlsBatch(
    clipIds: string[],
    options: PresignedUrlOptions = {}
  ): Promise<PresignedUrlResponse[]> {
    const promises = clipIds.map(clipId => this.getPresignedUrl(clipId, options));
    return Promise.all(promises);
  }

  /**
   * Clears the URL cache
   */
  public clearUrlCache(): void {
    this.urlCache.clear();
  }

  /**
   * Gets S3 clip metadata by ID
   * @param clipId The clip ID
   * @returns S3 clip metadata or undefined if not found
   */
  public getS3ClipMetadata(clipId: string): S3ClipMetadata | undefined {
    return this.s3ClipsIndex[clipId];
  }

  /**
   * Gets all S3 clip IDs
   * @returns Array of S3 clip IDs
   */
  public getS3ClipIds(): string[] {
    return Object.keys(this.s3ClipsIndex);
  }

  /**
   * Gets the total number of S3 clips
   * @returns Number of S3 clips
   */
  public getS3ClipCount(): number {
    return Object.keys(this.s3ClipsIndex).length;
  }

  /**
   * Checks if currently in S3 mode
   * @returns Whether S3 mode is enabled
   */
  public isS3ModeEnabled(): boolean {
    return this.useS3;
  }

  // === Private Helper Methods ===

  /**
   * Generates the actual presigned URL (to be implemented)
   * @param clipId The clip ID
   * @param options Presigned URL options
   * @returns Promise that resolves to presigned URL response
   */
  private async generatePresignedUrl(
    clipId: string, 
    options: PresignedUrlOptions
  ): Promise<PresignedUrlResponse> {
    const metadata = this.s3ClipsIndex[clipId];
    const expiresIn = options.expiresIn || 3600; // Default 1 hour
    const expiresAt = new Date(Date.now() + (expiresIn * 1000));

    // TODO: Replace with actual AWS SDK implementation
    // Example using AWS SDK v3:
    /*
    const { GetObjectCommand, S3Client } = require('@aws-sdk/client-s3');
    const { getSignedUrl } = require('@aws-sdk/s3-request-presigner');
    
    const client = new S3Client({
      region: this.s3Config!.region,
      credentials: {
        accessKeyId: this.s3Config!.accessKeyId!,
        secretAccessKey: this.s3Config!.secretAccessKey!,
        sessionToken: this.s3Config!.sessionToken
      }
    });

    const command = new GetObjectCommand({
      Bucket: this.s3Config!.bucketName,
      Key: metadata.key,
      ResponseContentDisposition: options.responseContentDisposition || 'inline',
      ResponseContentType: options.responseContentType || metadata.contentType
    });

    const url = await getSignedUrl(client, command, { expiresIn });
    */

    // Placeholder implementation
    const url = `https://${this.s3Config!.bucketName}.s3.${this.s3Config!.region}.amazonaws.com/${metadata.key}?X-Amz-Expires=${expiresIn}&X-Amz-SignedHeaders=host&X-Amz-Signature=placeholder`;

    return {
      url,
      expiresAt,
      clipId
    };
  }

  /**
   * Handles S3-related errors
   * @param error The error to handle
   * @returns Formatted S3ServiceError
   */
  private handleS3Error(error: any): S3ServiceError {
    if (error.name === 'NoSuchKey') {
      return {
        code: 'NOT_FOUND',
        message: 'Audio clip not found in S3 bucket',
        statusCode: 404
      };
    }

    if (error.name === 'AccessDenied') {
      return {
        code: 'ACCESS_DENIED',
        message: 'Access denied to S3 resource',
        statusCode: 403
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'Unknown S3 error occurred',
      statusCode: 500
    };
  }
}

// Export types for use in other files
export type { 
  Clip, 
  AudioClips, 
  S3Config, 
  S3ClipMetadata, 
  S3ClipsIndex, 
  PresignedUrlOptions, 
  PresignedUrlResponse, 
  S3ServiceError 
}; 